// src/belediye/pages/sistem/SistemRollerPage.js
//
// Roller ve İzinler — TEK SAYFA master-detail (yeniden tasarım).
//   • Sol bar : roller (öncelik ASC = en yetkiliden en yetkisize), pill satırlar
//               (Yönlendirilenler "Birimler" kartı deseni). İlk rol otomatik aktif.
//               Her satırda ⋮ → rol ayarları modalı (düzenle / pasif / sil).
//   • Sağ     : aktif rolün İZİN matrisi — kategori (kod öneki) başlıkları altında
//               kutucuk grid'i (satırda 4-5). Kutucuk: büyük Türkçe ad + silik kod.
//               Kategori başında "Tümünü seç / Kaldır". Üstte seçili/toplam sayacı.
//   • Alt     : yapışkan Kaydet / Vazgeç barı (değişiklik varken). Kaydedince taban
//               sıfırlanır (dirty=false). Katlanabilir "açılan sayfalar" önizlemesi.
//
// Korunan sözleşmeler (eski SistemRolDetayPage'ten): is_sensitive satır süper
// kullanıcı değilse KİLİTLİ (başkan dahil — o bir rol, süper kullanıcı değil);
// is_scoped_target seçili izinde kapsam <select>; kaydet = tam-değişim
// (updateRole permissions=[tam set] + permission_scopes); _warning → uyarı.
// Backend API/uçları AYNI — yalnız sunum yeniden düzenlendi.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PlusIcon,
  EllipsisVerticalIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  PencilSquareIcon,
  PowerIcon,
} from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import {
  fetchRoles,
  fetchFeatureActions,
  createRole,
  updateRole,
  deleteRole,
} from "../../services/sistemApi";
import { visibleNav } from "../../nav";
import PageHeader from "../../components/utils/PageHeader";
import SearchBox from "../../components/utils/SearchBox";
import BasicModal from "../../components/ui/BasicModal";
import BottomSheet from "../../components/utils/BottomSheet";
import "./SistemRoller.css";

// Masaüstü (≥900px) sol bar; altında roller alttan-çekmecede açılır.
const useIsMobile = (max = 899) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(`(max-width: ${max}px)`).matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mm = window.matchMedia(`(max-width: ${max}px)`);
    const onChange = (e) => setIsMobile(!!e.matches);
    if (mm.addEventListener) mm.addEventListener("change", onChange);
    else mm.addListener(onChange);
    setIsMobile(mm.matches);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", onChange);
      else mm.removeListener(onChange);
    };
  }, [max]);
  return isMobile;
};

// Kategori (kod öneki) → Türkçe başlık.
const GROUP_LABELS = {
  applications: "Başvurular",
  assignments: "Atamalar",
  staff: "Personel / Loglar",
  close_request: "Kapatma Onayı",
  citizen_delivery: "Belge Teslimi",
  halk_masasi: "Halk Masası",
  notifications: "Bildirimler",
  mesajlasma: "Mesajlaşma",
  sms: "SMS",
  reports: "Raporlar",
  dashboard: "Dashboard",
  surveys: "Anketler",
  files: "Dosyalar",
  users: "Kullanıcı Yönetimi",
  units: "Birim Yönetimi",
  neighborhoods: "Mahalleler",
  admin_boundaries: "İdari Sınırlar",
  citizens: "Vatandaşlar",
  settings: "Ayarlar",
  permissions: "İzin Kataloğu",
  roles: "Rol Yönetimi",
};

const SCOPE_LABELS = { all_units: "Tüm birimler", member_units: "Birim kapsamlı" };
const WORKSPACES = ["belediye", "sistem", "halk-masasi", "saha"];

// FAZ-17 sunum profili (dashboard varyantı) — backend UI_PROFILE_CHOICES aynası.
// Boş = varsayılan (operasyonel görünüm). Yalnız sunum; yetkiye etkisi yoktur.
const UI_PROFILE_OPTS = [
  { v: "", l: "Varsayılan (operasyonel)" },
  { v: "operasyonel", l: "Operasyonel — bugünkü görünüm" },
  { v: "yonetici", l: "Yönetici — stratejik / havuz görünüm" },
  { v: "halk_masasi", l: "Halk Masası — vatandaş sorgu deneyimi" },
  { v: "saha", l: "Saha — talep odaklı deneyim" },
];

// B1: hedef-alan izinleri için "bu rol kime yazabilir/atayabilir" kapsamı.
const KAPSAM_OPTS = [
  { v: "herkes", l: "Herkes" },
  { v: "birim", l: "Kendi birimi" },
  { v: "alt_agac", l: "Birimi + alt birimler" },
];

const prefixOf = (code) => String(code || "").split(".")[0];
const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
const backendError = (ex, fallback) => {
  const d = ex?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d) {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return fallback;
};

export default function SistemRollerPage() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { can, isSuperUser } = usePermissions();
  const canManage = can("roles.manage");

  const [roles, setRoles] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [permQ, setPermQ] = useState("");
  const [activeId, setActiveId] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [original, setOriginal] = useState(() => new Set());
  const [scopes, setScopes] = useState(() => ({}));
  const [originalScopes, setOriginalScopes] = useState(() => ({}));
  // Kullanıcının ELLE açtığı/kapadığı kategoriler (varsayılanı ters çevirir).
  const [userToggled, setUserToggled] = useState(() => new Set());
  const [showPreview, setShowPreview] = useState(false);

  const [roleModal, setRoleModal] = useState(null); // {mode:"create"|"edit", role?}

  const isMobile = useIsMobile(899);
  const [rolesSheetOpen, setRolesSheetOpen] = useState(false);

  // ── Veri yükleme ────────────────────────────────────────────────────────────
  const loadRoles = useCallback(async () => {
    const r = await fetchRoles();
    setRoles(Array.isArray(r) ? r : []);
    return Array.isArray(r) ? r : [];
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [, a] = await Promise.all([loadRoles(), fetchFeatureActions()]);
      setActions(Array.isArray(a) ? a : []);
    } catch {
      setError("Roller yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [loadRoles]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Öncelik ASC = en yetkiliden en yetkisize (sistem_yoneticisi=5 … saha=50).
  const sortedRoles = useMemo(
    () =>
      [...roles].sort(
        (a, b) =>
          (a.priority ?? 100) - (b.priority ?? 100) ||
          String(a.code).localeCompare(String(b.code), "tr")
      ),
    [roles]
  );

  const activeRole = useMemo(
    () => roles.find((r) => String(r.id) === String(activeId)) || null,
    [roles, activeId]
  );

  // Aktif rolü belirle: URL param > mevcut geçerli > ilk (en yetkili) rol.
  useEffect(() => {
    if (!roles.length) return;
    if (roleId && roles.some((r) => String(r.id) === String(roleId))) {
      setActiveId(String(roleId));
      return;
    }
    if (!activeId || !roles.some((r) => String(r.id) === String(activeId))) {
      setActiveId(String(sortedRoles[0].id));
    }
  }, [roles, roleId, sortedRoles, activeId]);

  // Aktif rol / roller değişince izin setini tabana çek (kaydet sonrası dahil).
  useEffect(() => {
    if (!activeRole) return;
    const init = new Set(activeRole.permissions || []);
    setSelected(new Set(init));
    setOriginal(new Set(init));
    const initScopes = activeRole.permission_scopes || {};
    setScopes({ ...initScopes });
    setOriginalScopes({ ...initScopes });
    // Rol değişince elle açma/kapama geçmişi sıfırlanır → akordiyon varsayılana
    // döner (yalnız seçili izni olan kategori açık, aşağıdaki collapsedByDefault).
    setUserToggled(new Set());
    setPermQ("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, roles]);

  // ── Kategori gruplama (backend module+code sırasını korur) ───────────────────
  const groups = useMemo(() => {
    const map = new Map();
    for (const a of actions) {
      const p = prefixOf(a.code);
      if (!map.has(p)) map.set(p, []);
      map.get(p).push(a);
    }
    return [...map.entries()].map(([prefix, items]) => ({ prefix, items }));
  }, [actions]);

  // İzin araması: kategori bazında eşleşen kutucukları süz; eşleşmeyen kategori gizlenir.
  const searching = permQ.trim().length > 0;
  const visibleGroups = useMemo(() => {
    const needle = permQ.trim().toLocaleLowerCase("tr");
    if (!needle) return groups.map((g) => ({ ...g, shown: g.items }));
    return groups
      .map((g) => ({
        ...g,
        shown: g.items.filter((a) =>
          `${a.name || ""} ${a.code}`.toLocaleLowerCase("tr").includes(needle)
        ),
      }))
      .filter((g) => g.shown.length > 0);
  }, [groups, permQ]);

  // Akordiyon varsayılanı: TÜM kategoriler kapalı gelir (izni olsun olmasın).
  // Kullanıcı elle açar → userToggled tersler; arama açıkken hepsi zorla açılır.
  const collapsedByDefault = useMemo(
    () => new Set(groups.map((g) => g.prefix)),
    [groups]
  );

  const rowLocked = (a) => !canManage || (a.is_sensitive && !isSuperUser);

  const scopesDirty = useMemo(
    () =>
      actions.some(
        (a) =>
          a.is_scoped_target &&
          selected.has(a.code) &&
          (scopes[a.code] || "herkes") !== (originalScopes[a.code] || "herkes")
      ),
    [actions, selected, scopes, originalScopes]
  );

  const dirty = !setsEqual(selected, original) || scopesDirty;

  const changeCount = useMemo(() => {
    let n = 0;
    new Set([...selected, ...original]).forEach((c) => {
      if (selected.has(c) !== original.has(c)) n += 1;
    });
    return n;
  }, [selected, original]);

  // ── İzin işlemleri ───────────────────────────────────────────────────────────
  const toggle = (code) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const toggleGroupAll = (items, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      items.forEach((a) => {
        if (rowLocked(a)) return; // kilitli satırlar değişmez
        if (on) next.add(a.code);
        else next.delete(a.code);
      });
      return next;
    });

  const setScope = (code, val) =>
    setScopes((prev) => ({ ...prev, [code]: val }));

  const toggleCollapse = (prefix) =>
    setUserToggled((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });

  const resetChanges = () => {
    setSelected(new Set(original));
    setScopes({ ...originalScopes });
  };

  const save = async () => {
    if (!activeRole) return;
    setSaving(true);
    try {
      const permission_scopes = {};
      actions.forEach((a) => {
        if (a.is_scoped_target && selected.has(a.code)) {
          permission_scopes[a.code] = scopes[a.code] || "herkes";
        }
      });
      const data = await updateRole(activeRole.id, {
        permissions: [...selected],
        permission_scopes,
      });
      if (data && data._warning) toast.warning(data._warning);
      else toast.success("İzinler kaydedildi.");
      await loadRoles(); // taban effect ile yeniden hesaplanır → dirty=false
    } catch (ex) {
      toast.error(backendError(ex, "İzinler kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  // ── Rol seçimi (kaydedilmemiş değişiklik koruması) ───────────────────────────
  const trySelect = (id) => {
    if (String(id) === String(activeId)) {
      setRolesSheetOpen(false);
      return;
    }
    if (
      dirty &&
      !window.confirm(
        "Bu rolde kaydedilmemiş izin değişiklikleri var. Yine de çıkılsın mı?"
      )
    )
      return;
    setActiveId(String(id));
    setRolesSheetOpen(false);
    navigate(`/yonetim/sistem/roller/${id}`);
  };

  // ── Rol CRUD (modal) ─────────────────────────────────────────────────────────
  const afterRoleSaved = async (savedId) => {
    setRoleModal(null);
    const list = await loadRoles();
    if (savedId && list.some((r) => String(r.id) === String(savedId))) {
      setActiveId(String(savedId));
      navigate(`/yonetim/sistem/roller/${savedId}`);
    }
  };

  const handleDeleteRole = async (role) => {
    if (
      !window.confirm(
        `"${role.name}" rolü silinsin mi? Bu işlem geri alınamaz.`
      )
    )
      return;
    try {
      await deleteRole(role.id);
      toast.success("Rol silindi.");
      setRoleModal(null);
      const list = await loadRoles();
      const next = [...list].sort(
        (a, b) => (a.priority ?? 100) - (b.priority ?? 100)
      )[0];
      if (next) {
        setActiveId(String(next.id));
        navigate(`/yonetim/sistem/roller/${next.id}`);
      }
    } catch (ex) {
      toast.error(backendError(ex, "Rol silinemedi."));
    }
  };

  const handleToggleActive = async (role) => {
    try {
      await updateRole(role.id, { is_active: !role.is_active });
      toast.success(role.is_active ? "Rol pasifleştirildi." : "Rol aktifleştirildi.");
      setRoleModal(null);
      await loadRoles();
    } catch (ex) {
      toast.error(backendError(ex, "Durum değiştirilemedi."));
    }
  };

  // ── B3: seçili izinlerle açılan sayfalar (canlı önizleme) ────────────────────
  const previewGroups = useMemo(() => {
    const arr = [...selected];
    const seen = new Map();
    WORKSPACES.forEach((w) => {
      visibleNav(arr, { workspace: w, menuOnly: false }).forEach((item) => {
        if (!seen.has(item.page)) seen.set(item.page, item);
      });
    });
    const byGroup = new Map();
    for (const item of seen.values()) {
      if (!byGroup.has(item.group)) byGroup.set(item.group, []);
      byGroup.get(item.group).push(item);
    }
    return [...byGroup.entries()];
  }, [selected]);

  // ── Başlık bilgi içeriği ─────────────────────────────────────────────────────
  const infoContent = (
    <ul className="syr-info-list">
      <li>
        <b>Sol taraf</b> — roller en yetkiliden en yetkisize sıralıdır. Bir role
        tıklayın; izinleri sağda görünür.
      </li>
      <li>
        <b>Sağ taraf</b> — izinler kategoriye göre gruplanır. Kutucuğa tıklayarak
        açıp kapatın; kategori başındaki <b>Tümünü seç / Kaldır</b> ile toplu
        değiştirin.
      </li>
      <li>
        <b>Kritik</b> izinler yalnız süper kullanıcı tarafından değiştirilebilir
        (başkan dahil hiçbir rol bunu geçemez).
      </li>
      <li>
        Değişiklikler alttaki <b>Kaydet</b> ile uygulanır; <b>Vazgeç</b> son
        kayıtlı hâle döndürür.
      </li>
    </ul>
  );

  // Roller listesi — hem masaüstü sol bar hem mobil alttan-çekmece kullanır.
  const renderRolesList = () => (
    <div className="syr-roles-list">
      {sortedRoles.length === 0 ? (
        <div className="syr-roles-empty">Henüz rol yok.</div>
      ) : (
        sortedRoles.map((r) => {
          const isActive = String(r.id) === String(activeId);
          return (
            <div
              key={r.id}
              className={`syr-role-row${isActive ? " active" : ""}`}
            >
              <button
                type="button"
                className="syr-role-hit"
                onClick={() => trySelect(r.id)}
                aria-pressed={isActive}
              >
                <span className="syr-role-main">
                  <span className="syr-role-name">{r.name}</span>
                  <span className="syr-role-sub">
                    {r.permissions?.length ?? 0} izin · {r.user_count ?? 0}{" "}
                    kullanıcı
                  </span>
                </span>
                <span className="syr-role-badges">
                  {r.is_system && (
                    <ShieldCheckIcon
                      width={14}
                      height={14}
                      className="syr-role-badge-ico"
                      title="Sistem rolü"
                    />
                  )}
                  {!r.is_active && (
                    <span className="md-chip md-chip-danger syr-role-passive">
                      Pasif
                    </span>
                  )}
                </span>
              </button>
              {canManage && (
                <button
                  type="button"
                  className="syr-role-kebab"
                  onClick={() => setRoleModal({ mode: "edit", role: r })}
                  aria-label={`${r.name} ayarları`}
                  title="Rol ayarları"
                >
                  <EllipsisVerticalIcon width={18} height={18} />
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="syr-root">
      <PageHeader
        title="Roller ve İzinler"
        infoTitle="Roller ve İzinler nasıl çalışır?"
        infoContent={infoContent}
        infoWide
      >
        <SearchBox
          value={permQ}
          onChange={setPermQ}
          placeholder="İzin ara (ad veya kod)…"
          className="syr-search"
        />
        {canManage && (
          <button
            type="button"
            className="syr-create-chip"
            onClick={() => setRoleModal({ mode: "create" })}
          >
            <PlusIcon width={16} height={16} />
            Rol Oluştur
          </button>
        )}
      </PageHeader>

      {loading ? (
        <div className="syr-state">Yükleniyor…</div>
      ) : error ? (
        <div className="syr-state syr-state-error">
          {error}
          <button className="btn-md btn-md-ghost" onClick={loadAll}>
            Tekrar dene
          </button>
        </div>
      ) : (
        <div className="syr-shell">
          {/* ── SOL: ROLLER (masaüstü sabit bar) ────────────────────────── */}
          {!isMobile && (
            <aside className="syr-roles">
              <div className="syr-roles-head">
                <span>Roller</span>
                <span className="syr-roles-count">{sortedRoles.length}</span>
              </div>
              {renderRolesList()}
            </aside>
          )}

          {/* ── SAĞ: İZİNLER ────────────────────────────────────────────── */}
          <main className="syr-perms">
            {/* Mobil: rol seçici → alttan çekmece */}
            {isMobile && (
              <button
                type="button"
                className="syr-roles-trigger"
                onClick={() => setRolesSheetOpen(true)}
              >
                <ShieldCheckIcon width={16} height={16} />
                <span className="syr-roles-trigger-label">Rol</span>
                <span className="syr-roles-trigger-name">
                  {activeRole?.name || "Seçin"}
                </span>
                <ChevronUpDownIcon width={16} height={16} />
              </button>
            )}

            {!activeRole ? (
              <div className="syr-state">Rol seçin.</div>
            ) : (
              <>
                <div className="syr-perms-head">
                  <div className="syr-perms-title-wrap">
                    <h2 className="syr-perms-title">
                      İzinler — {activeRole.name}
                    </h2>
                    <div className="syr-perms-meta">
                      <span className="syr-perms-code">{activeRole.code}</span>
                      {activeRole.is_system && (
                        <span className="md-chip">
                          <ShieldCheckIcon width={12} height={12} /> Sistem rolü
                        </span>
                      )}
                      <span className="md-chip">
                        {SCOPE_LABELS[activeRole.scope_mode] ||
                          activeRole.scope_mode}
                      </span>
                    </div>
                  </div>
                  <span className="syr-perm-counter">
                    <b>{selected.size}</b> / {actions.length} izin
                  </span>
                </div>

                {!canManage && (
                  <div className="syr-note syr-note-info">
                    Görüntüleme modu — izin değiştirmek için <b>roles.manage</b>{" "}
                    gerekir.
                  </div>
                )}
                {activeRole.is_system && canManage && (
                  <div className="syr-note syr-note-warn">
                    <ExclamationTriangleIcon width={16} height={16} />
                    Sistem rolü — izin çıkarmak bu roldeki TÜM kullanıcıları
                    anında etkiler.
                  </div>
                )}

                {/* Kategori grid'i (arama başlıktaki kutudan) */}
                {searching && visibleGroups.length === 0 ? (
                  <div className="syr-state">Eşleşen izin bulunamadı.</div>
                ) : null}
                <div className="syr-cats">
                  {visibleGroups.map(({ prefix, items, shown }) => {
                    const total = items.length;
                    const sel = items.filter((a) => selected.has(a.code)).length;
                    // arama açıkken hep açık; değilse varsayılan XOR elle-toggle
                    const collapsed = searching
                      ? false
                      : collapsedByDefault.has(prefix) !== userToggled.has(prefix);
                    const viewCode = items.find((a) =>
                      a.code.endsWith(".view")
                    )?.code;
                    const viewOn = viewCode ? selected.has(viewCode) : true;
                    const anyActionOn = items.some(
                      (a) => selected.has(a.code) && !a.code.endsWith(".view")
                    );
                    const actViewWarn = viewCode && !viewOn && anyActionOn;
                    const selectable = items.filter((a) => !rowLocked(a));
                    const allOn =
                      selectable.length > 0 &&
                      selectable.every((a) => selected.has(a.code));

                    return (
                      <section className="syr-cat" key={prefix}>
                        <div className="syr-cat-head">
                          <button
                            type="button"
                            className="syr-cat-title-btn"
                            onClick={() => toggleCollapse(prefix)}
                            aria-expanded={!collapsed}
                          >
                            <ChevronDownIcon
                              width={16}
                              height={16}
                              className={`syr-cat-chevron${
                                collapsed ? " collapsed" : ""
                              }`}
                            />
                            <span className="syr-cat-title">
                              {GROUP_LABELS[prefix] || prefix}
                            </span>
                            <span
                              className={`syr-cat-count${
                                sel > 0 ? " has" : ""
                              }`}
                            >
                              {sel}/{total}
                            </span>
                          </button>
                          {canManage && selectable.length > 0 && (
                            <div className="syr-cat-actions">
                              <button
                                type="button"
                                className="syr-cat-btn"
                                onClick={() => toggleGroupAll(items, true)}
                                disabled={allOn}
                              >
                                <CheckIcon width={13} height={13} /> Tümünü seç
                              </button>
                              <button
                                type="button"
                                className="syr-cat-btn"
                                onClick={() => toggleGroupAll(items, false)}
                                disabled={sel === 0}
                              >
                                <XMarkIcon width={13} height={13} /> Kaldır
                              </button>
                            </div>
                          )}
                        </div>

                        {actViewWarn && !collapsed && (
                          <div className="syr-actview-warn">
                            <ExclamationTriangleIcon width={14} height={14} />
                            Görüntüleme izni ({viewCode}) kapalı; işlem izinleri
                            tek başına yetersiz kalabilir.
                          </div>
                        )}

                        {!collapsed && (
                          <div className="syr-tiles">
                            {shown.map((a) => {
                              const on = selected.has(a.code);
                              const locked = rowLocked(a);
                              const showScope = a.is_scoped_target && on;
                              return (
                                <div
                                  key={a.code}
                                  className={`syr-tile${on ? " on" : ""}${
                                    locked ? " locked" : ""
                                  }${showScope ? " scoped-open" : ""}`}
                                >
                                  <label
                                    className="syr-tile-hit"
                                    title={
                                      locked && a.is_sensitive
                                        ? "Kritik yetki — yalnız süper kullanıcı değiştirebilir."
                                        : undefined
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      className="syr-tile-check"
                                      checked={on}
                                      disabled={locked}
                                      onChange={() => toggle(a.code)}
                                      aria-label={a.name || a.code}
                                    />
                                    <span className="syr-tile-body">
                                      <span className="syr-tile-name">
                                        <span className="syr-tile-name-txt">
                                          {a.name || a.code}
                                        </span>
                                        {a.is_sensitive && (
                                          <span
                                            className="syr-tile-sensitive"
                                            title="Kritik yetki — yalnız süper kullanıcı"
                                          >
                                            <LockClosedIcon
                                              width={11}
                                              height={11}
                                            />
                                          </span>
                                        )}
                                      </span>
                                      <span className="syr-tile-code">
                                        {a.code}
                                      </span>
                                    </span>
                                  </label>

                                  {showScope && (
                                    <div className="syr-tile-scope">
                                      <span className="syr-tile-scope-label">
                                        Kime yazabilir/atayabilir:
                                      </span>
                                      <select
                                        className="md-input syr-tile-scope-select"
                                        value={scopes[a.code] || "herkes"}
                                        disabled={locked}
                                        onChange={(e) =>
                                          setScope(a.code, e.target.value)
                                        }
                                        aria-label={`${a.name || a.code} kapsamı`}
                                      >
                                        {KAPSAM_OPTS.map((o) => (
                                          <option key={o.v} value={o.v}>
                                            {o.l}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>

                {/* Katlanabilir: bu izinlerle açılan sayfalar */}
                <section className="syr-preview">
                  <button
                    type="button"
                    className="syr-preview-toggle"
                    onClick={() => setShowPreview((s) => !s)}
                    aria-expanded={showPreview}
                  >
                    <ChevronDownIcon
                      width={16}
                      height={16}
                      className={`syr-cat-chevron${
                        showPreview ? "" : " collapsed"
                      }`}
                    />
                    Bu izinlerle açılan sayfalar
                    <span className="syr-preview-badge">
                      {previewGroups.reduce((n, [, it]) => n + it.length, 0)}
                    </span>
                  </button>
                  {showPreview &&
                    (previewGroups.length === 0 ? (
                      <div className="syr-preview-empty">
                        Seçili izinler hiçbir sayfayı açmıyor.
                      </div>
                    ) : (
                      <ul className="syr-preview-list">
                        {previewGroups.map(([group, items]) => (
                          <li key={group} className="syr-preview-group">
                            <span className="syr-preview-group-name">
                              {group}
                            </span>
                            <span className="syr-preview-pages">
                              {items.map((it) => (
                                <span
                                  key={it.page}
                                  className="md-chip syr-preview-chip"
                                >
                                  {it.href.split("/").pop() || it.page}
                                </span>
                              ))}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ))}
                </section>
              </>
            )}
          </main>
        </div>
      )}

      {/* Yapışkan kaydet barı */}
      {canManage && dirty && activeRole && (
        <div className="syr-savebar">
          <span className="syr-savebar-count">{changeCount} değişiklik</span>
          <div className="syr-savebar-actions">
            <button
              className="btn-md btn-md-ghost"
              onClick={resetChanges}
              disabled={saving}
            >
              Vazgeç
            </button>
            <button
              className="btn-md btn-md-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      )}

      {/* Mobil: roller alttan-çekmece (filtreler gibi) */}
      <BottomSheet
        open={isMobile && rolesSheetOpen}
        onClose={() => setRolesSheetOpen(false)}
        title="Roller"
      >
        {renderRolesList()}
      </BottomSheet>

      {/* Rol oluştur / düzenle modalı */}
      {roleModal && (
        <RoleModal
          mode={roleModal.mode}
          role={roleModal.role}
          isSuperUser={isSuperUser}
          onClose={() => setRoleModal(null)}
          onSaved={afterRoleSaved}
          onDelete={handleDeleteRole}
          onToggleActive={handleToggleActive}
        />
      )}
    </div>
  );
}

// ── Rol oluştur/düzenle modalı ────────────────────────────────────────────────
// Kimlik alanları (ad, kod, açıklama, kapsam, öncelik). Düzenlemede ayrıca
// Pasifleştir/Aktifleştir ve Sil. is_system rolde silme kilitli (backend de korur).
function RoleModal({
  mode,
  role,
  isSuperUser,
  onClose,
  onSaved,
  onDelete,
  onToggleActive,
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    code: role?.code || "",
    name: role?.name || "",
    description: role?.description || "",
    scope_mode: role?.scope_mode || "member_units",
    priority: role?.priority ?? 100,
    ui_profile: role?.ui_profile || "",
    rank: role?.rank ?? 100,
    show_in_staff_directory: role?.show_in_staff_directory ?? true,
    is_system: role?.is_system ?? false,
    is_active: role?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.checked }));

  // Sistem rolünde superuser olmayan için kod + kapsam kilitli (backend R1 korur).
  const coreLocked = isEdit && role?.is_system && !isSuperUser;

  const submit = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = {
        ...form,
        priority: Number(form.priority) || 100,
        rank: Number(form.rank) || 100,
        ui_profile: form.ui_profile || null,
      };
      const saved = isEdit
        ? await updateRole(role.id, payload)
        : await createRole(payload);
      toast.success(isEdit ? "Rol güncellendi." : "Rol oluşturuldu.");
      onSaved(saved?.id ?? role?.id);
    } catch (ex) {
      setErr(backendError(ex, isEdit ? "Rol güncellenemedi." : "Rol oluşturulamadı."));
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <div className="syr-modal-foot">
      {isEdit && (
        <div className="syr-modal-foot-left">
          <button
            type="button"
            className="btn-md btn-md-ghost"
            onClick={() => onToggleActive(role)}
            disabled={saving}
          >
            <PowerIcon width={16} height={16} />
            {role.is_active ? "Pasifleştir" : "Aktifleştir"}
          </button>
          {!role.is_system && (
            <button
              type="button"
              className="btn-md btn-md-danger"
              onClick={() => onDelete(role)}
              disabled={saving}
            >
              <TrashIcon width={16} height={16} />
              Sil
            </button>
          )}
        </div>
      )}
      <div className="syr-modal-foot-right">
        <button
          type="button"
          className="btn-md btn-md-ghost"
          onClick={onClose}
          disabled={saving}
        >
          Vazgeç
        </button>
        <button
          type="button"
          className="btn-md btn-md-primary"
          onClick={submit}
          disabled={saving}
        >
          {saving ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
        </button>
      </div>
    </div>
  );

  return (
    <BasicModal
      open
      onClose={onClose}
      size="md"
      title={
        <span className="syr-modal-title">
          {isEdit ? (
            <>
              <PencilSquareIcon width={18} height={18} /> Rol ayarları
            </>
          ) : (
            <>
              <PlusIcon width={18} height={18} /> Yeni Rol
            </>
          )}
        </span>
      }
      actions={actions}
    >
      <div className="syr-form">
        {err && <div className="syr-alert">{err}</div>}
        {isEdit && role?.is_system && (
          <div className="syr-note syr-note-warn">
            <ExclamationTriangleIcon width={16} height={16} />
            Sistem rolü — çekirdek alanlar yalnız süper kullanıcı tarafından
            değiştirilebilir.
          </div>
        )}
        <label className="syr-field">
          <span>Rol kodu</span>
          <input
            className="md-input"
            value={form.code}
            onChange={set("code")}
            placeholder="örn: operasyon_koordinatoru"
            disabled={coreLocked}
            required
            autoFocus={!isEdit}
          />
        </label>
        <label className="syr-field">
          <span>Ad</span>
          <input className="md-input" value={form.name} onChange={set("name")} required />
        </label>
        <label className="syr-field">
          <span>Açıklama (opsiyonel)</span>
          <textarea
            className="md-input"
            value={form.description}
            onChange={set("description")}
            rows={2}
          />
        </label>
        <div className="syr-field-row">
          <label className="syr-field">
            <span>Kapsam</span>
            <select
              className="md-select"
              value={form.scope_mode}
              onChange={set("scope_mode")}
              disabled={coreLocked}
            >
              <option value="member_units">Birim kapsamlı (üyelik)</option>
              <option value="all_units">Tüm birimler</option>
            </select>
          </label>
          <label className="syr-field">
            <span>Öncelik (sıralama)</span>
            <input
              className="md-input"
              type="number"
              value={form.priority}
              onChange={set("priority")}
            />
          </label>
        </div>

        <div className="syr-field-row">
          <label className="syr-field">
            <span>Ekran profili (UI)</span>
            <select
              className="md-select"
              value={form.ui_profile}
              onChange={set("ui_profile")}
            >
              {UI_PROFILE_OPTS.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.l}
                </option>
              ))}
            </select>
          </label>
          <label className="syr-field">
            <span>Atama rütbesi (rank)</span>
            <input
              className="md-input"
              type="number"
              value={form.rank}
              onChange={set("rank")}
            />
          </label>
        </div>
        <p className="syr-hint">
          Ekran profili yalnız görünümü değiştirir (yetkiye etkisi yok). Rank =
          atama rütbesi: küçük sayı üst makam; talep yalnız daha büyük rank'lı
          (alt) kişilere atanabilir.
        </p>

        {/* Bayraklar */}
        <label className="syr-check-field">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={setBool("is_active")}
          />
          <span className="syr-check-body">
            <span className="syr-check-title">Aktif</span>
            <span className="syr-check-desc">
              Pasif roller yeni atamalarda seçilemez.
            </span>
          </span>
        </label>

        <label className="syr-check-field">
          <input
            type="checkbox"
            checked={form.show_in_staff_directory}
            onChange={setBool("show_in_staff_directory")}
          />
          <span className="syr-check-body">
            <span className="syr-check-title">Personel dizininde listele</span>
            <span className="syr-check-desc">
              Bu role sahip kişiler Personel dizininde listelensin mi?
              Yönetim/sistem rolleri için kapatılır; süper kullanıcı zaten
              listelenmez. Yetkiye etkisi yoktur.
            </span>
          </span>
        </label>

        {isSuperUser && (
          <label className="syr-check-field">
            <input
              type="checkbox"
              checked={form.is_system}
              onChange={setBool("is_system")}
            />
            <span className="syr-check-body">
              <span className="syr-check-title">Sistem rolü</span>
              <span className="syr-check-desc">
                Çekirdek (seed) rol. Silinemez; temel alanları yalnız süper
                kullanıcı değiştirir. Yalnız süper kullanıcı işaretleyebilir.
              </span>
            </span>
          </label>
        )}

        <p className="syr-hint">
          {isEdit
            ? "İzinler sağdaki matristen atanır; buradan yalnız kimlik/ayar alanları düzenlenir."
            : "Oluşturduktan sonra izinleri sağdaki matristen atarsınız."}
        </p>
      </div>
    </BasicModal>
  );
}
